import { Box, Button, FormControl, InputLabel, MenuItem, Modal, Select, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { FinancialCategoryInterface } from "../../common/interfaces";
import { FinancialEntryType, FinancialEntryTypeLabel } from "../../components/enum/financialEnums";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PageToaster from "../toaster/PageToaster";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  category?: FinancialCategoryInterface | null;
  /**
   * Empresa escolhida pelo ADMIN na tela. A categoria precisa nascer na
   * empresa selecionada, não na do ADMIN. Perfis comuns não mandam nada: o
   * backend ignora este campo e usa a empresa do token.
   */
  companyId?: number | null;
}

const FinancialCategoryCreateModal: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  category,
  companyId
}) => {
  const [name, setName] = useState("");
  const [type, setType] = useState<FinancialEntryType>(FinancialEntryType.EXPENSE);
  const [color, setColor] = useState("#2b7bff");

  const updateMode = Boolean(category);
  const containerId = "toaster-financial-category-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    if (category) {
      setName(category.name);
      setType(category.type as FinancialEntryType);
      setColor(category.color ?? "#2b7bff");
    } else {
      setName("");
      setType(FinancialEntryType.EXPENSE);
      setColor("#2b7bff");
    }
  }, [category, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      autoError("Informe o nome da categoria.");
      return;
    }
    try {
      const payload = { name: name.trim(), type, color };
      if (updateMode && category) {
        await api.patch(`/financial/categories/${category.financial_category_id}`, payload);
        autoSuccess("Categoria atualizada com sucesso!");
      } else {
        await api.post("/financial/categories", {
          ...payload,
          company_id: companyId ?? undefined
        });
        autoSuccess("Categoria criada com sucesso!");
      }
      onSaved();
      onClose();
    } catch (error) {
      handleHttpError(error);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 380
          }}
        >
          <h2>{updateMode ? "Editar categoria" : "Nova categoria"}</h2>

          <TextField
            fullWidth
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={type}
              onChange={(e) => setType(e.target.value as FinancialEntryType)}
            >
              {Object.values(FinancialEntryType).map((t) => (
                <MenuItem key={t} value={t}>
                  {FinancialEntryTypeLabel[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Cor"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            margin="normal"
          />

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              {updateMode ? "Atualizar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FinancialCategoryCreateModal;
